// Subscription service for managing user subscription data and API calls
import { api } from './api'
import { authService } from './auth'

export interface SubscriptionStatus {
  status: string
  plan_id?: string
  expires_at?: string
  stripe_customer_id?: string
  current_period_start?: string
  current_period_end?: string
}

export interface SubscriptionDetails {
  subscription_id: string
  customer_id: string
  status: string
  plan: {
    id: string
    name: string
    amount: number
    currency: string
    interval: string
  }
  current_period_start: string
  current_period_end: string
  cancel_at_period_end: boolean
}

export interface PlanInfo {
  id: string
  name: string
  price: number
  currency: string
  interval: 'month' | 'year'
  features: string[]
  available: boolean
}

class SubscriptionService {
  /**
   * Get current subscription status from backend
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    try {
      const response = await api.get('/api/subscription/status')
      return response as SubscriptionStatus
    } catch (error) {
      console.error('Failed to fetch subscription status:', error)
      throw error
    }
  }

  /**
   * Check if user can access specific feature
   */
  async checkFeatureAccess(feature: string): Promise<boolean> {
    try {
      const response = await api.get(`/api/subscription/check-access?feature=${feature}`)
      return (response as any).has_access === true
    } catch (error) {
      console.error('Failed to check feature access:', error)
      return false
    }
  }

  /**
   * Get available subscription plans
   */
  async getAvailablePlans(): Promise<PlanInfo[]> {
    try {
      const response = await api.get('/api/subscription/plans')
      return response as PlanInfo[]
    } catch (error) {
      console.error('Failed to fetch subscription plans:', error)
      return []
    }
  }

  /**
   * Get detailed subscription information
   */
  async getSubscriptionDetails(): Promise<SubscriptionDetails | null> {
    try {
      const response = await api.get('/api/subscription/details')
      return response as SubscriptionDetails
    } catch (error) {
      console.error('Failed to fetch subscription details:', error)
      return null
    }
  }

  /**
   * Create Stripe customer portal session for subscription management
   */
  async createCustomerPortalSession(): Promise<{ url: string }> {
    try {
      const response = await api.post('/api/stripe/create-portal-session', {})
      return response as { url: string }
    } catch (error) {
      console.error('Failed to create customer portal session:', error)
      throw error
    }
  }

  /**
   * Get user's subscription status from local storage (cached)
   */
  getCachedSubscriptionStatus(): string {
    const user = authService.getUser()
    return user?.subscription_status || 'free'
  }

  /**
   * Check if user has premium access (cached check)
   */
  hasPremiumAccess(): boolean {
    return authService.canAccessPremiumFeatures()
  }

  /**
   * Get subscription expiration date
   */
  getExpirationDate(): Date | null {
    const user = authService.getUser()
    if (!user?.subscription_end_date) return null

    try {
      const date = new Date(user.subscription_end_date)
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return null
      }
      return date
    } catch (error) {
      console.error('Failed to parse subscription end date:', error)
      return null
    }
  }

  /**
   * Check if subscription is expired
   */
  isSubscriptionExpired(): boolean {
    const expiration = this.getExpirationDate()
    if (!expiration) return false
    
    return new Date() > expiration
  }

  /**
   * Get days until subscription expires
   */
  getDaysUntilExpiration(): number | null {
    const expiration = this.getExpirationDate()
    if (!expiration) return null
    
    const now = new Date()
    const diffTime = expiration.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    return diffDays
  }

  /**
   * Format subscription status for display
   */
  formatSubscriptionStatus(status: string): string {
    const statusMap: Record<string, string> = {
      'active': 'Subscrição Ativa',
      'trial': 'Período de Teste',
      'past_due': 'Pagamento em Atraso',
      'canceled': 'Subscrição Cancelada',
      'incomplete': 'Pagamento Incompleto',
      'free': 'Conta Gratuita'
    }
    
    return statusMap[status] || status
  }

  /**
   * Get subscription color for UI display
   */
  getSubscriptionStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'active': '#10b981', // green
      'trial': '#3b82f6', // blue
      'past_due': '#f59e0b', // amber
      'canceled': '#ef4444', // red
      'incomplete': '#f59e0b', // amber
      'free': '#6b7280' // gray
    }
    
    return colorMap[status] || '#6b7280'
  }

  /**
   * Create billing portal session (alias for customer portal)
   */
  async createBillingPortalSession(): Promise<{ url: string }> {
    return this.createCustomerPortalSession()
  }

  /**
   * Get user invoices from Stripe
   */
  async getInvoices(): Promise<any[]> {
    try {
      const response = await api.get('/api/stripe/invoices')
      return response as any[]
    } catch (error) {
      console.error('Failed to fetch invoices:', error)
      throw error
    }
  }

  /**
   * Cancel subscription at period end
   */
  async cancelSubscription(): Promise<{success: boolean, message: string, cancel_at_period_end: boolean}> {
    try {
      const response = await api.post('/api/stripe/cancel-subscription', {})
      return response as {success: boolean, message: string, cancel_at_period_end: boolean}
    } catch (error) {
      console.error('Failed to cancel subscription:', error)
      throw error
    }
  }
}

// Export singleton instance
export const subscriptionService = new SubscriptionService()
